import { useEffect, useMemo, useRef, useState } from 'react';
import { Map as GoogleMapComponent } from 'google-maps-react-markers';
import Image from 'next/image';
import { CUSTOM_COURT_IMG_PLACEHOLDER_SRC } from 'constants/media';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { getGoogleMapsAddressUrl } from 'utils/shared/location/getGoogleMapsAddressUrl';
import { useMobileView } from 'hooks/useMobileView';
import { useModal } from 'hooks/useModal';
import CourtFlat from 'svg/CourtFlat';
import CrossPhoto from 'svg/CrossPhoto';
import { ButtonLink } from 'components/Button';
import GoogleMap from 'components/GoogleMap';
import Modal from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';
import AddressSelectionCard from './AddressSelectionCard';
import VenueMapCard from './VenueMapCard';
import type { MapVenue, VenueLocationsMapProps } from './types';

const MAP_PADDING_PX = 48;

/**
 * A map of venues with a selectable address list beneath it. Selection is shared
 * three ways — clicking a marker, clicking a row, and the initial default — and
 * shows as a popup card on desktop or a bottom sheet on mobile.
 *
 * Lifted out of the coach page so the player profile gets the same behaviour; the
 * two differ only in the card's CTA and whether distances can be measured.
 */
export default function VenueLocationsMap({
  venues,
  distanceFrom,
  renderAction,
  fitToMarkers = false,
  maxZoom,
  zoom = 12,
  center,
  options,
  mapClassName = 'h-[21rem]',
  placeholderImageSrc = CUSTOM_COURT_IMG_PLACEHOLDER_SRC,
}: VenueLocationsMapProps) {
  const bottomSheetModal = useModal();
  const isMobile = useMobileView();
  const mapRef = useRef<GoogleMapComponent>();

  // Desktop shows the first venue's card immediately; mobile must not open a sheet
  // unprompted, so the two selections are tracked apart.
  const [activeVenueDesktop, setActiveVenueDesktop] = useState<MapVenue | null>(null);
  const [activeVenueMobile, setActiveVenueMobile] = useState<MapVenue | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);

  const activeVenue = isMobile ? activeVenueMobile : activeVenueDesktop;

  const defaultCenter = useMemo(
    () =>
      center ?? (venues.length ? { lat: venues[0].latitude, lng: venues[0].longitude } : undefined),
    [center, venues],
  );

  const distanceTo = (venue: MapVenue) =>
    distanceFrom
      ? Math.round(
          calculateHaversineDistance({
            coord1: { latitude: venue.latitude, longitude: venue.longitude },
            coord2: distanceFrom,
            unit: 'miles',
          }),
        )
      : null;

  useEffect(() => {
    if (!venues.length || !isMapReady || !mapRef.current) return;

    if (fitToMarkers && window.google?.maps) {
      const bounds = new window.google.maps.LatLngBounds();
      venues.forEach((venue) => bounds.extend({ lat: venue.latitude, lng: venue.longitude }));
      mapRef.current.fitBounds(bounds, MAP_PADDING_PX);

      if (maxZoom != null) {
        window.google.maps.event.addListenerOnce(mapRef.current, 'idle', () => {
          if ((mapRef.current?.getZoom() ?? 0) > maxZoom) mapRef.current?.setZoom(maxZoom);
        });
      }
      return;
    }

    mapRef.current.setCenter({ lat: venues[0].latitude, lng: venues[0].longitude });
  }, [venues, isMapReady, fitToMarkers, maxZoom]);

  useEffect(() => {
    if (venues.length && !isMobile) setActiveVenueDesktop(venues[0]);
  }, [venues, isMobile]);

  useEffect(() => {
    if (activeVenueMobile) bottomSheetModal.openModal();
  }, [activeVenueMobile]);

  const handleBottomSheetClose = () => {
    bottomSheetModal.closeModal();
    setActiveVenueMobile(null);
  };

  const selectVenue = (venue: MapVenue, { recenter = false } = {}) => {
    if (isMobile) {
      setActiveVenueMobile(venue);
    } else {
      setActiveVenueDesktop(venue);
    }

    if (recenter) {
      mapRef.current?.setCenter({ lat: venue.latitude, lng: venue.longitude });
    }
  };

  const activeVenueDistance = activeVenue ? distanceTo(activeVenue) : null;

  return (
    <>
      <div className="relative border-color-border-input-lightmode dark:border-color-border-input-darkmode">
        <div
          className={classNames(
            'overflow-hidden rounded-xl bg-color-text-lightmode-placeholder dark:bg-color-text-darkmode-placeholder',
            mapClassName,
          )}
        >
          <GoogleMap
            defaultCenter={defaultCenter}
            options={options}
            zoom={zoom}
            mapId={process.env.GOOGLE_MAPS_MAP_ID}
            onGoogleApiLoaded={(map) => {
              mapRef.current = map;
              setIsMapReady(true);
            }}
            markers={venues.map((venue) => {
              const isActive = activeVenue?.id === venue.id;

              return {
                lat: venue.latitude,
                lng: venue.longitude,
                zIndex: isActive ? 2 : 1,
                onClick: () => selectVenue(venue),
                children: (
                  <>
                    <div
                      className={classNames(
                        'absolute grid h-10 w-10 cursor-pointer place-items-center rounded-full',
                        isActive
                          ? 'bg-color-brand-primary'
                          : 'bg-color-bg-lightmode-primary shadow-sm dark:bg-color-bg-darkmode-primary',
                      )}
                    >
                      <CourtFlat
                        className={classNames(
                          'h-5 w-5',
                          isActive
                            ? 'text-color-bg-lightmode-primary dark:text-color-bg-darkmode-primary'
                            : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
                        )}
                      />
                    </div>
                    {!isMobile && isActive && (
                      <div className="absolute left-7 top-1/2 ml-4 -translate-y-1/2 transform">
                        <VenueMapCard
                          venue={venue}
                          distance={distanceTo(venue)}
                          action={renderAction?.(venue, 'card')}
                          placeholderImageSrc={placeholderImageSrc}
                        />
                      </div>
                    )}
                  </>
                ),
              };
            })}
          />
        </div>
      </div>

      {venues.length > 0 && (
        <div className="mt-4 flex flex-col gap-ds-sm">
          {venues.map((venue) => (
            <AddressSelectionCard
              key={venue.id}
              address={venue.address}
              isActive={activeVenue?.id === venue.id}
              onSelect={() => selectVenue(venue, { recenter: true })}
            />
          ))}
        </div>
      )}

      <Modal
        positionBottomDesktop
        isOpen={bottomSheetModal.isOpen}
        handleClose={handleBottomSheetClose}
        swipeProps={{
          onSwipedDown: handleBottomSheetClose,
          delta: 50,
          trackMouse: true,
          trackTouch: true,
        }}
      >
        <div className="flex min-h-96 flex-col bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary">
          <div className="flex h-16 items-center justify-between px-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            <span className="typography-product-heading-mobile">Location</span>
            <span onClick={handleBottomSheetClose}>
              <CrossPhoto className="h-5 w-5 cursor-pointer" />
            </span>
          </div>
          <div className="relative mx-4 h-44 overflow-hidden rounded-md bg-gray-50">
            <Image src={activeVenue?.imageUrl || placeholderImageSrc} alt="" fill />
          </div>
          <div className="mt-2 px-4">
            <span className="typography-product-heading-compact-desktop">
              {activeVenue?.title || activeVenue?.address}
            </span>
            <div className="typography-product-caption flex items-center gap-1 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              {activeVenue?.isIndoor != null && (
                <span>{activeVenue.isIndoor ? 'Indoor' : 'Outdoor'}</span>
              )}
              {activeVenue?.isIndoor != null && activeVenueDistance != null && <span>&bull;</span>}
              {activeVenueDistance != null && (
                <span>
                  {activeVenueDistance} mile{activeVenueDistance !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <ButtonLink
              isExternal
              href={getGoogleMapsAddressUrl(activeVenue?.address ?? '')}
              variant="inverted"
              sizeMobile="lg"
              className="mb-4 mt-10"
            >
              Check the location
            </ButtonLink>
            {activeVenue && <div className="mb-5">{renderAction?.(activeVenue, 'sheet')}</div>}
          </div>
        </div>
      </Modal>
    </>
  );
}
